import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';

import { FiscalYear } from 'models';
import { LedgerAccount } from 'models/LedgerAccount';

export interface Navigation<T extends { Id: string }> {
  prev: T | null;
  current: T | null;
  next: T | null;
}

interface BudgetComparisonState {
  endDate: Date | null;
  expanded: boolean;
  selectedFiscalYear: FiscalYear | null;
  invoiceId: string | null;
  selectedLedgerAccount: LedgerAccount | null;
  startDate: Date | null;
}

const initialState: BudgetComparisonState = {
  endDate: null,
  selectedFiscalYear: null,
  startDate: null,
  expanded: true,
  selectedLedgerAccount: null,
  invoiceId: null,
};

export const cooperativeBudgetSlice = createSlice({
  name: 'budget-comparison',
  initialState,
  reducers: {
    changeSelectedFiscalYear: (
      state,
      action: PayloadAction<FiscalYear | null>
    ) => {
      state.selectedFiscalYear = action.payload;
    },
    changeEndDate: (state, action: PayloadAction<Date | null>) => {
      state.endDate = action.payload;
    },
    changeStartDate: (state, action: PayloadAction<Date | null>) => {
      state.startDate = action.payload;
    },
    changeInvoiceId: (state, action: PayloadAction<string | null>) => {
      state.invoiceId = action.payload;
    },
    changeSelectedLedgerAccount: (
      state,
      action: PayloadAction<LedgerAccount | null>
    ) => {
      state.selectedLedgerAccount = action.payload;
    },
    toggleExpandedView: (state) => {
      state.expanded = !state.expanded;
    },
    reset: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  changeEndDate,
  changeInvoiceId,
  changeSelectedFiscalYear,
  changeStartDate,
  toggleExpandedView,
  changeSelectedLedgerAccount,
  reset,
} = cooperativeBudgetSlice.actions;
export default persistReducer<BudgetComparisonState, any>(
  {
    key: 'budgetComparison',
    storage,
    blacklist: ['selectedLedgerAccount', 'invoiceId', 'expanded'],
    transforms: [
      createTransform(
        (value, key) => {
          if (key === 'startDate' || key === 'endDate') {
            return value ? (value as Date).getTime() : value;
          }

          return value;
        },
        (value, key) => {
          if (key === 'startDate' || key === 'endDate') {
            return value ? new Date(value) : value;
          }

          return value;
        }
      ),
    ],
  },
  cooperativeBudgetSlice.reducer
);
